import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useYMaps } from '@pbe/react-yandex-maps';
import ImportPanel from './ImportPanel';
import { URL } from '../../utils/constants';
import './DrawPanel.css';

export default function DrawPanel({
  mapRef, metroLineNumbers, mode, setMode, onSave, username, isOckPicking = false,
  embedded = false,
  forceCollapsed,
  onCollapsedChange,
  activateExclusiveTool,
  releaseExclusiveTool,
  registerExclusiveToolStop,
  unregisterExclusiveToolStop,
  activeExclusiveTool,
  isToolBlocked,
  exclusiveTools,
}) {
  // ---- доступ только по группе "only add" ----
  const [canUsePanel, setCanUsePanel] = useState(false);

  // ---- id текущего пользователя, нужен для проставления author ----
  const [userId, setUserId] = useState(null);

  // ---- сворачивание/разворачивание панели ----
  const [collapsedLocal, setCollapsedLocal] = useState(() => {
    try { return localStorage.getItem('draw_panel_collapsed') === '1'; } catch { return false; }
  });
  const collapsed = (typeof forceCollapsed === "boolean") ? forceCollapsed : collapsedLocal;
  const setCollapsed = (vOrFn) => {
    const next = typeof vOrFn === "function" ? vOrFn(collapsed) : vOrFn;
    if (typeof forceCollapsed === "boolean") {
      onCollapsedChange?.(next);
    } else {
      setCollapsedLocal(next);
    }
  };
  useEffect(() => {
    if (typeof forceCollapsed === "boolean") return;
    try { localStorage.setItem('draw_panel_collapsed', collapsed ? '1' : '0'); } catch {}
  }, [collapsed, forceCollapsed]);

  useEffect(() => {
    const norm = (s) => String(s ?? '').toLowerCase().trim();

    const applyFromObj = (u = {}) => {
      const uid =
        u?.id ??
        u?.pk ??
        u?.user?.id ??
        (Array.isArray(u) && u.length ? (u[0]?.id ?? u[0]?.pk) : null);

      if (uid && !userId) setUserId(uid);

      let groups = u.groups || u.group_names || [];
      if (Array.isArray(groups) && groups.length && typeof groups[0] === 'object') {
        groups = groups.map(g => g?.name);
      }
      const hasOnlyAdd = groups.map(norm).includes('only add');
      if (hasOnlyAdd) setCanUsePanel(true);
      return hasOnlyAdd;
    };

    // 1) localStorage
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      if (u && (u.id || u.pk || (u.user && u.user.id))) {
        applyFromObj(u);
      }
    } catch {}

    // 2) попытка получить себя через API (только для чтения групп/ид)
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    const endpoints = [
      `${URL}/api/users/me/`,
      `${URL}/users/me/`,
      `${URL}/api/auth/users/me/`
    ];

    (async () => {
      for (const ep of endpoints) {
        try {
          const r = await fetch(ep, { headers: { Authorization: `Token ${token}` } });
          if (!r.ok) continue;
          const data = await r.json();
          applyFromObj(data);
          break;
        } catch {}
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- ХУКИ ДОЛЖНЫ БЫТЬ НЕУСЛОВНЫМИ ---
  const [name, setName] = useState('');
  const [line, setLine] = useState('');
  const [segments, setSegments] = useState([{ id: 1, coords: [], closeLoop: false }]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [isDrawing, setIsDrawing] = useState(false);
  const [showImport, setShowImport] = useState(false);

  // грузим редактор только если разрешено
  const ymaps = useYMaps(['Polyline']);
  const clickHandlerRef = useRef(null);
  const hiddenRef = useRef([]);
  const lastClickRef = useRef(0);
  const segPolysRef = useRef([]);
  const activeIdxRef = useRef(activeIdx);
  useEffect(() => { activeIdxRef.current = activeIdx; }, [activeIdx]);

  const isOckPickingRef = useRef(isOckPicking);
  useEffect(() => {
    isOckPickingRef.current = isOckPicking;
  }, [isOckPicking]);

  const getStrokeColor = () => (mode === 'linesections' ? '#DE64A1' : '#000000');

  const ensureSegmentGraphics = (i) => {
    if (!canUsePanel) return;
    const map = mapRef.current;
    if (!map || !ymaps) return;
    if (!segPolysRef.current[i]) {
      const outline = new ymaps.Polyline([], {}, { strokeColor: '#000000', strokeWidth: 6, opacity: 1, zIndex: 999 });
      const color   = new ymaps.Polyline([], {}, { strokeColor: getStrokeColor(), strokeWidth: 4, opacity: 0.9, zIndex: 1000 });
      map.geoObjects.add(outline);
      map.geoObjects.add(color);
      segPolysRef.current[i] = { outline, color };
    } else {
      segPolysRef.current[i].color.options.set('strokeColor', getStrokeColor());
    }
    const coords = segments[i]?.coords || [];
    segPolysRef.current[i]?.outline.geometry.setCoordinates(coords);
    segPolysRef.current[i]?.color.geometry.setCoordinates(coords);
  };

  const refreshOutlineVisibility = () => {
    if (!canUsePanel) return;
    segPolysRef.current.forEach((p, i) => p && p.outline.options.set('opacity', i === activeIdx ? 1 : 0));
  };

  const stopDrawingSession = () => {
    if (!canUsePanel) return;
    const map = mapRef.current;
    if (!map) return;

    // Корректно снимаем обработчик клика карты
    if (clickHandlerRef.current) {
      try { map.events.remove('click', clickHandlerRef.current); } catch {}
      clickHandlerRef.current = null;
    }

    // Возвращаем интерактивность только тем объектам, которые реально прятали
    hiddenRef.current.forEach(({ obj, prev }) => {
      try { obj?.options?.set?.('interactivityModel', prev); } catch {}
    });
    hiddenRef.current = [];
  };

  const drawBlocked = isToolBlocked?.(exclusiveTools.DRAW);

  const stopDrawTool = useCallback(() => {
    try {
      stopDrawingSession();
    } catch {}

    setIsDrawing(false);
    releaseExclusiveTool?.(exclusiveTools.DRAW);
  }, [releaseExclusiveTool]);

  useEffect(() => {
    if (!registerExclusiveToolStop || !unregisterExclusiveToolStop) return;

    registerExclusiveToolStop(exclusiveTools.DRAW, stopDrawTool);

    return () => {
      unregisterExclusiveToolStop(exclusiveTools.DRAW, stopDrawTool);
    };
  }, [registerExclusiveToolStop, unregisterExclusiveToolStop, stopDrawTool]);

  useEffect(() => {
    if (!canUsePanel) return;
    const map = mapRef.current;
    if (!map || !ymaps) return;

    if (!isDrawing) {
      stopDrawingSession();
      return;
    }

    hiddenRef.current = [];

    const drawObjects = new Set(
      segPolysRef.current.flatMap((p) => [p?.outline, p?.color]).filter(Boolean)
    );

    map.geoObjects.each((obj) => {
      if (!obj?.options) return;

      // Не трогаем собственные линии DrawPanel
      if (drawObjects.has(obj)) return;

      // Не трогаем ОСК placemark (по caption)
      try {
        if (obj?.properties?.get?.('__isOCKPoint')) return;
      } catch {}

      // Не трогаем halo ОСК (Circle)
      try {
        const ctorName = obj?.constructor?.name || '';
        if (ctorName.includes('Circle')) return;
      } catch {}

      const prev = obj.options.get('interactivityModel') || 'default#opaque';
      hiddenRef.current.push({ obj, prev });

      try {
        obj.options.set('interactivityModel', 'default#transparent');
      } catch {}
    });

    segments.forEach((_, i) => ensureSegmentGraphics(i));
    refreshOutlineVisibility();

    clickHandlerRef.current = (e) => {
      if (isOckPickingRef.current) return;

      const now = Date.now();
      if (now - lastClickRef.current < 10) return;
      lastClickRef.current = now;

      const pt = e.get('coords');
      if (!pt || isOckPickingRef.current) return;

      const idx = activeIdxRef.current;

      setSegments((prev) => {
        const next = prev.map((seg, i) => (i === idx ? { ...seg, coords: [...seg.coords, pt] } : seg));
        const pair = segPolysRef.current[idx];
        if (pair) {
          const coords = next[idx].coords;
          pair.outline.geometry.setCoordinates(coords);
          pair.color.geometry.setCoordinates(coords);
        }
        return next;
      });
    };

    map.events.add('click', clickHandlerRef.current);

    return () => stopDrawingSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawing, ymaps, mapRef, activeIdx, segments.length, mode, canUsePanel]);

  useEffect(() => {
    if (!canUsePanel) return;
    segPolysRef.current.forEach((p) => p && p.color.options.set('strokeColor', getStrokeColor()));
  }, [mode, canUsePanel]);

  useEffect(() => {
    if (!canUsePanel) return;
    segments.forEach((_, i) => ensureSegmentGraphics(i));
    refreshOutlineVisibility();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segments, canUsePanel]);

  useEffect(() => {
    if (!canUsePanel) return;
    refreshOutlineVisibility();
  }, [activeIdx, isDrawing, canUsePanel]);

  useEffect(() => {
    if (isOckPicking && isDrawing) {
      stopDrawTool();
    }
  }, [isOckPicking, isDrawing, stopDrawTool]);

  const handleImport = (importedCoords) => {
    if (!canUsePanel) return;
    const map = mapRef.current;
    if (!map || !ymaps) return;

    setSegments((prev) => {
      const next = prev.map((seg, i) => (i === activeIdx ? { ...seg, coords: importedCoords } : seg));
      ensureSegmentGraphics(activeIdx);
      const pair = segPolysRef.current[activeIdx];
      if (pair) {
        pair.outline.geometry.setCoordinates(importedCoords);
        pair.color.geometry.setCoordinates(importedCoords);
      }
      const bounds = pair?.color.geometry.getBounds();
      if (bounds) map.setBounds(bounds, { checkZoomRange: true });
      return next;
    });
  };

  const handleRemovePoint = (segIndex, pointIndex) => {
    if (!canUsePanel) return;
    setSegments((prev) => {
      const next = prev.map((seg, i) =>
        i !== segIndex ? seg : { ...seg, coords: seg.coords.filter((_, j) => j !== pointIndex) }
      );
      const pair = segPolysRef.current[segIndex];
      if (pair) {
        pair.outline.geometry.setCoordinates(next[segIndex].coords);
        pair.color.geometry.setCoordinates(next[segIndex].coords);
      }
      return next;
    });
  };

  const handleClearAll = () => {
    if (!mapRef.current) return;
    const idx = activeIdxRef.current;
    segPolysRef.current.forEach((pair) => {
      if (!pair) return;
      try { mapRef.current.geoObjects.remove(pair.outline); } catch {}
      try { mapRef.current.geoObjects.remove(pair.color); } catch {}
    });
    segPolysRef.current = [];
    ensureSegmentGraphics(idx);
    setSegments([{ id: 1, coords: [], closeLoop: false }]);
    setActiveIdx(0);
  };

  const addSegment = () => {
    if (!canUsePanel) return;
    setSegments((prev) => [...prev, { id: prev.length + 1, coords: [], closeLoop: false }]);
    setActiveIdx((prev) => prev + 1);
  };

  const handleSave = () => {
    if (!canUsePanel) return;

    if (!userId) {
      alert('Не удалось определить ID пользователя: сохранение с привязкой автора недоступно. Проверьте авторизацию.');
      return;
    }

    let any = false;
    segments.forEach((seg) => {
      if (seg.coords.length > 0) {
        any = true;

        const coords = seg.closeLoop && seg.coords.length > 2
          ? [...seg.coords, seg.coords[0]]
          : seg.coords;

        onSave({
          name,
          line,
          coords,
          mode,
          author: userId,
          author_id: userId,
          isPolygone: mode === 'linesections' ? !!seg.closeLoop : undefined,
          closeLoop: !!seg.closeLoop,
        });
      }
    });

    if (!any) return;
    handleClearAll();
    stopDrawTool();
    setName('');
    setLine('');
  };

  // Если прав нет — не показываем панель и кнопку
  if (!canUsePanel) return null;

  const canSave = !!(name && line && segments.some(s => s.coords.length > 0));
  const hasAnyPoints = segments.some(s => s.coords.length);

  return (
    <>
      {!embedded && (
        <div className="drawpanel-toggle-wrap">
          <button
            className="drawpanel-toggle-btn"
            aria-expanded={!collapsed}
            title={collapsed ? 'Развернуть панель рисования' : 'Свернуть панель рисования'}
            onClick={() => setCollapsed(v => !v)}
          >
            {collapsed ? 'Панель рисования ▸' : 'Панель рисования ▾'}
          </button>
        </div>
      )}

      {showImport && !collapsed && (
        <ImportPanel
          map={mapRef.current}
          ymaps={ymaps}
          mode={mode}
          onCancel={() => setShowImport(false)}
          onImport={handleImport}
        />
      )}

      {!collapsed && (
        <div className={`drawpanel ${embedded ? "drawpanel--embedded" : ""}`}>
          <button
            className={`drawpanel-btn ${isDrawing ? 'drawpanel-btn--cancel' : 'drawpanel-btn--primary'} drawpanel-btn--md ${isDrawing ? 'drawpanel-btn--mb8' : 'drawpanel-btn--mb16'}`}
            onClick={() => {
              if (drawBlocked) return;

              if (isDrawing) {
                stopDrawTool();
                return;
              }

              activateExclusiveTool?.(exclusiveTools.DRAW);
              setIsDrawing(true);
            }}
            disabled={drawBlocked}
          >
            {isDrawing ? 'Завершить черчение' : 'Начать черчение'}
          </button>

          {isDrawing && (
            <div className="drawpanel-mode-box">
              <label className="drawpanel-mode-label drawpanel-mode-label--mb4">
                <input
                  className="drawpanel-radio"
                  type="radio"
                  name="mode"
                  value="linesections"
                  checked={mode === 'linesections'}
                  onChange={() => setMode('linesections')}
                />
                <span className="drawpanel-mode-text">Линия</span>
              </label>

              <label className="drawpanel-mode-label">
                <input
                  className="drawpanel-radio"
                  type="radio"
                  name="mode"
                  value="stations"
                  checked={mode === 'stations'}
                  onChange={() => setMode('stations')}
                />
                <span className="drawpanel-mode-text">Станция</span>
              </label>
            </div>
          )}

          {isDrawing && (
            <button
              className="drawpanel-btn drawpanel-btn--primary drawpanel-btn--md drawpanel-btn--mb8"
              onClick={() => setShowImport(true)}
            >
              Импортировать точки (в активную)
            </button>
          )}

          <input
            className="drawpanel-input"
            placeholder="Название"
            value={name}
            onChange={e => setName(e.target.value)}
          />

          <select
            className="drawpanel-select"
            value={line}
            onChange={e => setLine(e.target.value)}
          >
            <option value="">‒ линия метро ‒</option>
            {Object.keys(metroLineNumbers).map(l => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>

          <button
            className="drawpanel-btn drawpanel-btn--success drawpanel-btn--sm drawpanel-btn--mb8"
            disabled={!canSave}
            onClick={handleSave}
            style={!canSave ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
          >
            Сохранить
          </button>

          <button
            className="drawpanel-btn drawpanel-btn--cancel drawpanel-btn--sm drawpanel-btn--mb12"
            disabled={!hasAnyPoints}
            onClick={handleClearAll}
            style={!hasAnyPoints ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
          >
            Удалить все точки
          </button>

          <div className="drawpanel-segments">
            {segments.map((seg, i) => (
              <div
                key={seg.id}
                className={`drawpanel-segment ${i === activeIdx ? 'drawpanel-segment--active' : ''}`}
              >
                <div className="drawpanel-segment-head">
                  <strong>Линия {i + 1}</strong>
                  {i !== activeIdx ? (
                    <button
                      onClick={() => setActiveIdx(i)}
                      className="drawpanel-inline-btn"
                    >
                      Активировать
                    </button>
                  ) : (
                    <span className="drawpanel-segment-active-text">активная</span>
                  )}
                </div>

                <div className="drawpanel-segment-subhead">
                  <div className="drawpanel-points-count">Точек: {seg.coords.length}</div>
                  <label className="drawpanel-loop-label">
                    <input
                      type="checkbox"
                      checked={!!seg.closeLoop}
                      onChange={(e) =>
                        setSegments(prev =>
                          prev.map((s, idx) => idx === i ? { ...s, closeLoop: e.target.checked } : s)
                        )
                      }
                    />
                    Зациклить
                  </label>
                </div>

                {seg.coords.length === 0 && (
                  <div className="drawpanel-empty">
                    Точек нет — добавьте кликом на карту или импортируйте.
                  </div>
                )}

                {seg.coords.map((pt, idx) => (
                  <div key={idx} className="drawpanel-point-row">
                    <span className="drawpanel-point-text">
                      {idx + 1}. Ш: {pt[0].toFixed(6)}, Д: {pt[1].toFixed(6)}
                    </span>
                    <button
                      onClick={() => handleRemovePoint(i, idx)}
                      title="Удалить точку"
                      className="drawpanel-point-delete"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            ))}
          </div>

          <button
            className="drawpanel-btn drawpanel-btn--primary drawpanel-btn--sm drawpanel-add-segment"
            onClick={addSegment}
          >
            Добавить линию
          </button>
        </div>
      )}
    </>
  );
}